<template>
  <WizardModal
    ref="wizardRef"
    v-model="currentStep"
    :show-modal="show"
    :modal-title="t('Cadastro de Novo Usuário')"
    :steps="wizardSteps"
    size="large"
    :loading="isLoading"
    :custom-buttons="true"
    :next-label="nextButtonLabel"
    :disable-next="disableNextButton"
    @finish="handleFinish"
    @cancel="handleCancel"
    @close="handleClose"
  >
    <!-- Step 0: Registration -->
    <template #step-0>
      <OnboardingStep0Registration
        v-model="formData"
        :errors="formErrors"
        @validate="handleStep0Validate"
      />
    </template>

    <!-- Step 1: Permissions (Groups) -->
    <template #step-1>
      <OnboardingStep1Permissions
        v-model="selectedGroups"
        :available-groups="availableGroups"
        :loading="loadingGroups"
        :valores-de-atributo="{ mcp_scopes: formData.mcp_scopes, dw_scopes: formData.dw_scopes }"
        @update:valores-de-atributo="aplicaValoresDeAtributo"
      />
    </template>

    <!-- Step 2: Franchises (Conditional) -->
    <template #step-2>
      <OnboardingStep2Franchises
        v-if="!pulaFranquias"
        v-model="selectedFranchises"
        :available-franchises="availableFranchises"
        :loading="loadingFranchises"
      />
    </template>

    <!-- Step 3: Profile Picture -->
    <template #step-3>
      <OnboardingStep3ProfilePic
        v-model="profileImage"
        :username="formData.username"
      />
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModal from '@/ui/WizardModal.vue';
import OnboardingStep0Registration from './OnboardingStep0Registration.vue';
import OnboardingStep1Permissions from './OnboardingStep1Permissions.vue';
import OnboardingStep2Franchises from './OnboardingStep2Franchises.vue';
import OnboardingStep3ProfilePic from './OnboardingStep3ProfilePic.vue';
import { useUsers } from '@/composables/useUsers';
import { useRegrasDeGrupos } from '@/composables/useRegrasDeGrupos';
import { permissionsService } from '@/services/permissionsService';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

interface FormData {
  username: string;
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone_number: string;
  documento: string;
  language: string;
  versaoSistema: string;
  bgcolor: string;
  mcp_scopes: string;
  dw_scopes: string;
}

interface FormErrors {
  [field: string]: string | undefined;
}

interface AvailableFranchise {
  id: string;
  name: string;
  location: string;
  status: string;
}

interface FranchiseEntry {
  Dados?: { Nome?: string; Cidade?: string; [key: string]: unknown };
  Nome?: string;
  Cidade?: string;
  [key: string]: unknown;
}

interface CreatedUser {
  username?: string;
  sub?: string;
  [key: string]: unknown;
}

interface WizardStep {
  title: string;
  description: string;
  validate: () => boolean;
}

const { t } = useI18n();
const {
  createUser,
  updateUser,
  toggleUserGroup,
  toggleUserFranquia,
  uploadProfileImage,
  fetchUserGroups
} = useUsers();

// Props
const props = defineProps<{
  show: boolean;
}>();

// Emits
const emit = defineEmits<{
  close: [];
  complete: [user: CreatedUser];
}>();

// Wizard state
const wizardRef = ref<unknown>(null);
const currentStep = ref<number>(0);
const isLoading = ref<boolean>(false);

// Form data
const formData = ref<FormData>({
  username: '',
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  phone_number: '',
  documento: '',
  language: 'pt-BR',
  versaoSistema: '0',
  bgcolor: '#007bff',
  mcp_scopes: '',
  dw_scopes: ''
});

/** Os editores de acesso (skill app-groups) devolvem o mapa atributo → valor; o formulário guarda os dois campos do contrato. */
const aplicaValoresDeAtributo = (valores: Record<string, string>): void => {
  formData.value.mcp_scopes = valores.mcp_scopes ?? '';
  formData.value.dw_scopes = valores.dw_scopes ?? '';
};

const formErrors = ref<FormErrors>({});
const isStep0Valid = ref<boolean>(false);

// Permissions data
const selectedGroups = ref<string[]>([]);
const availableGroups = ref<string[]>([]);
const loadingGroups = ref<boolean>(false);

// Franchises data
const selectedFranchises = ref<string[]>([]);
const availableFranchises = ref<AvailableFranchise[]>([]);
const loadingFranchises = ref<boolean>(false);

// Profile picture
const profileImage = ref<File | null>(null);

// Created user data (stored after step 0)
const createdUser = ref<CreatedUser | null>(null);

// Computed
// Pula a etapa de franquias (skill app-groups): conta Full (vê todas), quem está cadastrando não
// vincula franquia (administração delegada — capacidade do app-api) ou a conta só tem grupos de
// app (no app ela vê o que o app decide; na plataforma, nenhuma unidade).
const { carregar: carregarRegras, deveLimpar, soGruposDeApp } = useRegrasDeGrupos();
void carregarRegras();
const pulaFranquias = computed<boolean>(
  () => selectedGroups.value.includes('Full')
    || !permissionsService.hasCapability('vincula_franquias')
    || soGruposDeApp(selectedGroups.value)
);

// Wizard steps configuration
const wizardSteps = computed<WizardStep[]>(() => [
  {
    title: t('Cadastro'),
    description: t('Dados básicos do usuário'),
    validate: () => isStep0Valid.value
  },
  {
    title: t('Permissões'),
    description: t('O que a pessoa faz'),
    validate: () => selectedGroups.value.length > 0
  },
  {
    title: t('Franquias'),
    description: t('Acesso por franquia'),
    validate: () => pulaFranquias.value || selectedFranchises.value.length > 0
  },
  {
    title: t('Foto de Perfil'),
    description: t('Imagem do usuário (opcional)'),
    validate: () => true // Optional step
  }
]);

// Button configuration
const nextButtonLabel = computed(() => {
  return (step: number): string => {
    if (isLoading.value) {
      return t('Processando...');
    }

    switch (step) {
      case 0:
        return t('Criar Usuário');
      case 1:
        return pulaFranquias.value ? t('Próximo (Pular Franquias)') : t('Próximo');
      case 2:
        return t('Próximo');
      case 3:
        return profileImage.value ? t('Finalizar com Foto') : t('Finalizar sem Foto');
      default:
        return t('Próximo');
    }
  };
});

const disableNextButton = computed(() => {
  return (step: number): boolean => {
    if (isLoading.value) return true;

    const stepConfig = wizardSteps.value[step];
    return Boolean(stepConfig && !stepConfig.validate());
  };
});

// Load available groups on mount
onMounted(async () => {
  try {
    loadingGroups.value = true;
    availableGroups.value = await fetchUserGroups();
    log('UserOnboardingWizard', 'onMounted', 'Groups loaded', availableGroups.value);
  } catch (error: unknown) {
    log('UserOnboardingWizard', 'onMounted', 'Error loading groups', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar grupos de usuários'));
  } finally {
    loadingGroups.value = false;
  }
});

// Load franchises
const loadFranchises = async (): Promise<void> => {
  loadingFranchises.value = true;
  try {
    const franchises = await permissionsService.getFranquias() as Record<string, FranchiseEntry>;

    // Convert to array format expected by OnboardingStep2Franchises
    availableFranchises.value = Object.keys(franchises || {}).map(id => ({
      id: id,
      name: franchises[id]?.Dados?.Nome || franchises[id]?.Nome || 'Sem nome',
      location: franchises[id]?.Dados?.Cidade || franchises[id]?.Cidade || '',
      status: 'active' // Assume all franchises are active
    }));

    log('UserOnboardingWizard', 'loadFranchises', 'Franchises loaded', availableFranchises.value);
  } catch (error: unknown) {
    log('UserOnboardingWizard', 'loadFranchises', 'Error loading franchises', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar franquias'));
  } finally {
    loadingFranchises.value = false;
  }
};

// Watch for step changes to perform async operations
watch(currentStep, async (newStep, oldStep) => {
  log('UserOnboardingWizard', 'step-change', 'Step changed', { from: oldStep, to: newStep });

  // Prevent automatic advancement - we handle it manually
  if (newStep > oldStep + 1) {
    return;
  }

  // Handle step completion with validation
  if (newStep === oldStep + 1) {
    switch (oldStep) {
      case 0:
        await handleStep0Complete();
        break;
      case 1:
        // Validate before completing step 1
        if (selectedGroups.value.length === 0) {
          eventBus.emit('globalMessage.error', t('Selecione ao menos um grupo'));
          currentStep.value = 1; // Stay on current step
          return;
        }
        await handleStep1Complete();
        break;
      case 2:
        // pulaFranquias short-circuits franchise assignment in handleStep1Complete:
        // it already routed us straight to step 3, so re-running
        // handleStep2Complete here would throw on the missing sub.
        if (pulaFranquias.value) {
          break;
        }
        // Validate before completing step 2
        if (selectedFranchises.value.length === 0) {
          eventBus.emit('globalMessage.error', t('Selecione ao menos uma franquia'));
          currentStep.value = 2; // Stay on current step
          return;
        }
        await handleStep2Complete();
        break;
    }
  }
}, { immediate: false });

// Step 0: User Registration
const handleStep0Validate = (isValid: boolean, errors: FormErrors): void => {
  isStep0Valid.value = isValid;
  formErrors.value = errors;
};

const handleStep0Complete = async (): Promise<void> => {
  // Idempotency guard: navigating back to step 0 and forward again would
  // otherwise re-fire AdminCreateUser and surface UsernameExistsException.
  if (createdUser.value?.sub) {
    log('UserOnboardingWizard', 'handleStep0Complete', 'User already created, skipping');
    return;
  }
  try {
    isLoading.value = true;
    log('UserOnboardingWizard', 'handleStep0Complete', 'Creating user', formData.value);

    // Create user
    const response = await createUser({
      username: formData.value.username,
      name: formData.value.name,
      email: formData.value.email,
      password: formData.value.password,
      phone_number: formData.value.phone_number,
      documento: formData.value.documento,
      language: formData.value.language,
      bgcolor: formData.value.bgcolor,
      mcp_scopes: formData.value.mcp_scopes
    });

    // criaUsuario now returns { created, sub } in the dados envelope so the
    // franchise-assignment step (which needs the sub) works for non-Full users.
    createdUser.value = {
      username: formData.value.username,
      ...(typeof response === 'object' && response !== null ? response : {})
    } as CreatedUser;

    log('UserOnboardingWizard', 'handleStep0Complete', 'User created successfully', createdUser.value);

    eventBus.emit('globalMessage.success', t('Usuário criado com sucesso'));

  } catch (error: unknown) {
    log('UserOnboardingWizard', 'handleStep0Complete', 'Error creating user', error);
    const message = error instanceof Error ? error.message : t('Erro ao criar usuário');
    eventBus.emit('globalMessage.error', message);

    // Stay on current step for retry
    currentStep.value = 0;
  } finally {
    isLoading.value = false;
  }
};

// Step 1: Permissions Assignment
const handleStep1Complete = async (): Promise<void> => {
  try {
    isLoading.value = true;
    log('UserOnboardingWizard', 'handleStep1Complete', 'Assigning groups', selectedGroups.value);

    if (!createdUser.value?.username) {
      throw new Error(t('Usuário não foi criado'));
    }

    // Assign all selected groups
    for (const group of selectedGroups.value) {
      await toggleUserGroup(createdUser.value.username, group);
    }

    log('UserOnboardingWizard', 'handleStep1Complete', 'Groups assigned successfully');

    // The access attributes (MCP tools, warehouse sources…) are picked in this step, after the
    // user exists and has its groups — the app-api only accepts an attribute on an account that
    // has the group unlocking it (skill app-groups). Without that group it is cleared.
    if (deveLimpar('mcp_scopes', selectedGroups.value)) {
      formData.value.mcp_scopes = '';
    }
    if (deveLimpar('dw_scopes', selectedGroups.value)) {
      formData.value.dw_scopes = '';
    }
    if (formData.value.mcp_scopes || formData.value.dw_scopes) {
      await updateUser({
        username: formData.value.username,
        name: formData.value.name,
        email: formData.value.email,
        phone_number: formData.value.phone_number,
        documento: formData.value.documento,
        language: formData.value.language,
        versaoSistema: formData.value.versaoSistema,
        bgcolor: formData.value.bgcolor,
        // Only what was set: a pool without the attribute rejects the whole update if the key is present.
        ...(formData.value.mcp_scopes ? { mcp_scopes: formData.value.mcp_scopes } : {}),
        ...(formData.value.dw_scopes ? { dw_scopes: formData.value.dw_scopes } : {})
      });
      log('UserOnboardingWizard', 'handleStep1Complete', 'Access attributes persisted');
    }

    // Navigate based on Full permission
    if (pulaFranquias.value) {
      // Skip franchises, go directly to profile picture
      log('UserOnboardingWizard', 'handleStep1Complete', 'Full permission detected, skipping franchises');
      currentStep.value = 3;
    } else {
      // Load available franchises for next step
      await loadFranchises();
    }

  } catch (error: unknown) {
    log('UserOnboardingWizard', 'handleStep1Complete', 'Error assigning groups', error);
    const message = error instanceof Error ? error.message : t('Erro ao atribuir permissões');
    eventBus.emit('globalMessage.error', message);

    // Stay on current step for retry
    currentStep.value = 1;
  } finally {
    isLoading.value = false;
  }
};

// Step 2: Franchises Assignment
const handleStep2Complete = async (): Promise<void> => {
  try {
    isLoading.value = true;
    log('UserOnboardingWizard', 'handleStep2Complete', 'Assigning franchises', selectedFranchises.value);

    if (!createdUser.value?.sub) {
      throw new Error(t('Usuário não foi criado'));
    }

    // Assign all selected franchises
    for (const franchiseId of selectedFranchises.value) {
      await toggleUserFranquia(createdUser.value.sub, Number(franchiseId));
    }

    log('UserOnboardingWizard', 'handleStep2Complete', 'Franchises assigned successfully');

  } catch (error: unknown) {
    log('UserOnboardingWizard', 'handleStep2Complete', 'Error assigning franchises', error);
    const message = error instanceof Error ? error.message : t('Erro ao atribuir franquias');
    eventBus.emit('globalMessage.error', message);

    // Stay on current step for retry
    currentStep.value = 2;
  } finally {
    isLoading.value = false;
  }
};

// Finish wizard
const handleFinish = async (): Promise<void> => {
  try {
    isLoading.value = true;
    log('UserOnboardingWizard', 'handleFinish', 'Finishing wizard');

    // Upload profile image if provided
    if (profileImage.value && createdUser.value?.sub) {
      try {
        log('UserOnboardingWizard', 'handleFinish', 'Uploading profile image');
        await uploadProfileImage(createdUser.value.sub, profileImage.value);
        log('UserOnboardingWizard', 'handleFinish', 'Profile image uploaded successfully');
      } catch (error: unknown) {
        log('UserOnboardingWizard', 'handleFinish', 'Error uploading image (non-critical)', error);

        // Image upload is non-critical, show warning but continue
        eventBus.emit('globalMessage.error', t('Usuário criado mas falha no upload da imagem'));
      }
    }

    // Success!
    eventBus.emit('globalMessage.success', t('Usuário configurado com sucesso'));

    // Refresh users table
    eventBus.emit('users.refresh');

    // Close wizard and emit complete
    if (createdUser.value) {
      emit('complete', createdUser.value);
    }
    emit('close');

  } catch (error: unknown) {
    log('UserOnboardingWizard', 'handleFinish', 'Error finishing wizard', error);
    const message = error instanceof Error ? error.message : t('Erro ao finalizar cadastro');
    eventBus.emit('globalMessage.error', message);
  } finally {
    isLoading.value = false;
  }
};

// Cancel wizard
const handleCancel = (): void => {
  log('UserOnboardingWizard', 'handleCancel', 'Wizard cancelled');

  // TODO: Consider cleanup if user was created but wizard was cancelled
  // For now, just close
  emit('close');
};

// Close wizard
const handleClose = (): void => {
  log('UserOnboardingWizard', 'handleClose', 'Wizard closed');
  emit('close');
};
</script>

<style scoped>
/* Wizard-specific styles can be added here if needed */
</style>
